import React from "react";
import { Icon } from "@iconify/react";
import { Button } from "antd";
import "./layouts-styles.css";

/**
 * The last line between one broken screen and a blank browser window.
 *
 * React unmounts the whole tree when a render throws, and nothing was
 * catching that — so a single bad value on one page took the sidebar,
 * the header and every route with it, leaving white. Wrapped around the
 * routed content, a failure now costs the page it happened on and
 * nothing else, and says so.
 *
 * `resetKey` changes with the route, so navigating away from a screen
 * that failed clears the error rather than leaving the message pinned in
 * place for the rest of the session.
 */
class BaseErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Nothing here reaches the user; it is what a developer needs when
    // they are handed a screenshot of the message below.
    console.error("Unhandled error while rendering a page", error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.failed && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (! this.state.failed) {
      return this.props.children;
    }

    return (
      <div className="state-block is-error">
        <span className="state-mark">
          <Icon icon="tabler:alert-triangle" width={ 28 } />
        </span>
        <span className="state-title">This page didn't load</span>
        <p className="state-desc">
          Something went wrong while showing it. The rest of the app is still
          working — try again, or move to another page.
        </p>
        <div className="state-action">
          <Button onClick={ () => this.setState({ failed: false }) }>Try again</Button>
        </div>
      </div>
    );
  }
}

export default BaseErrorBoundary;
